import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { api } from '@/api/client';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';
import { today } from '@/utils/helpers';
import { Plus, Trash, Search } from 'lucide-react';
import { ProductPicker } from '@/components/ui/ProductPicker';

const QUOTATION_STATUSES = ['Draft', 'Sent', 'Accepted', 'Rejected', 'Expired'];

const emptyItem = () => ({
  product_id: '',
  product_name: '',
  description: '',
  qty: 1,
  price: 0
});

const defaultForm = () => ({
  customer_id: '',
  customer_name: '',
  customer_phone: '',
  customer_email: '',
  items: [emptyItem()],
  discount: 0,
  tax: 0,
  notes: '',
  valid_until: '',
  status: 'Draft'
});

export const QuotationForm = ({ isOpen, onClose, quotation = null, onSaved }) => {
  const currency = useCurrency();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [formData, setFormData] = useState(defaultForm());

  useEffect(() => {
    if (isOpen) {
      loadData();
      if (quotation) {
        setFormData({
          customer_id: quotation.customer_id || '',
          customer_name: quotation.customer_name || '',
          customer_phone: quotation.customer_phone || '',
          customer_email: quotation.customer_email || '',
          items: quotation.items?.length ? quotation.items : [emptyItem()],
          discount: quotation.discount || 0,
          tax: quotation.tax || 0,
          notes: quotation.notes || '',
          valid_until: quotation.valid_until || '',
          status: quotation.status || 'Draft'
        });
      } else {
        setFormData(defaultForm());
      }
    }
  }, [isOpen, quotation]);

  const loadData = async () => {
    try {
      const [cRes, pRes] = await Promise.all([api.getCustomers(), api.getProducts()]);
      setCustomers(cRes.data);
      setProducts(pRes.data);
    } catch (e) { console.error(e); }
  };

  const updateItem = (idx, key, val) => {
    const items = [...formData.items];
    items[idx] = { ...items[idx], [key]: val };
    setFormData({ ...formData, items });
  };

  const onProductPick = (idx, productId) => {
    if (!productId) {
      updateItem(idx, 'product_id', '');
      return;
    }
    const p = products.find(x => x.id === productId);
    const items = [...formData.items];
    items[idx] = {
      ...items[idx],
      product_id: productId,
      product_name: p?.name || items[idx].product_name,
      price: p?.price || items[idx].price,
    };
    setFormData({ ...formData, items });
  };

  const addItem = () => setFormData({ ...formData, items: [...formData.items, emptyItem()] });
  const removeItem = (i) => {
    if (formData.items.length === 1) return;
    setFormData({ ...formData, items: formData.items.filter((_, idx) => idx !== i) });
  };

  const onCustomerPick = (customerId) => {
    if (!customerId) {
      setFormData({ ...formData, customer_id: '', customer_name: '', customer_phone: '', customer_email: '' });
      return;
    }
    const c = customers.find(x => x.id === customerId);
    setFormData({
      ...formData,
      customer_id: customerId,
      customer_name: c?.name || '',
      customer_phone: c?.phone || '',
      customer_email: c?.email || ''
    });
  };

  const subtotal = useMemo(() =>
    formData.items.reduce((s, it) => s + ((it.qty || 0) * (it.price || 0)), 0),
    [formData.items]
  );
  const discountAmt = parseFloat(formData.discount) || 0;
  const taxPct = parseFloat(formData.tax) || 0;
  const afterDiscount = Math.max(0, subtotal - discountAmt);
  const taxAmt = afterDiscount * (taxPct / 100);
  const total = afterDiscount + taxAmt;

  const productAvailability = (productId) => {
    if (!productId) return null;
    const p = products.find(x => x.id === productId);
    if (!p) return null;
    const inStock = (p.serials || []).filter(s => s.status === 'In Stock').length;
    return { name: p.name, inStock, price: p.price };
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone.includes(customerSearch)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_name) { alert('Customer name is required'); return; }
    if (!formData.items.length || formData.items.every(it => !it.product_name)) {
      alert('Add at least one line item');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        discount: discountAmt,
        tax: taxPct,
        items: formData.items.filter(it => it.product_name).map(it => ({
          product_id: it.product_id || '',
          product_name: it.product_name,
          description: it.description || '',
          qty: parseInt(it.qty) || 1,
          price: parseFloat(it.price) || 0
        }))
      };
      if (quotation) {
        await api.updateQuotation(quotation.id, payload);
      } else {
        await api.createQuotation(payload);
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      alert('Failed to save quotation');
    } finally { setLoading(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={quotation ? `Edit ${quotation.qt_number}` : 'New Quotation'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Customer */}
        <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Search size={14} className="text-gray-400" />
            <Input
              placeholder="Search existing customer by name or phone..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              data-testid="qt-customer-search"
            />
          </div>
          {customerSearch && (
            <Select
              value={formData.customer_id}
              onChange={(e) => onCustomerPick(e.target.value)}
              data-testid="qt-customer-select"
            >
              <option value="">-- Select Existing or fill manually --</option>
              {filteredCustomers.map(c => <option key={c.id} value={c.id}>{`${c.name} (${c.phone})`}</option>)}
            </Select>
          )}
          <div className="grid grid-cols-3 gap-3 mt-3">
            <Input
              label="Customer Name *"
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              data-testid="qt-customer-name"
            />
            <Input
              label="Phone"
              value={formData.customer_phone}
              onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              data-testid="qt-customer-phone"
            />
            <Input
              label="Email"
              value={formData.customer_email}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              data-testid="qt-customer-email"
            />
          </div>
        </div>

        {/* Items */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Line Items</label>
            <Button type="button" size="sm" variant="ghost" onClick={addItem} data-testid="qt-add-item">
              <Plus size={14} className="mr-1" /> Add Item
            </Button>
          </div>
          <div className="space-y-2">
            {formData.items.map((item, idx) => {
              const avail = productAvailability(item.product_id);
              return (
                <div key={idx} className="grid grid-cols-12 gap-2 p-2 border border-gray-200 dark:border-gray-700 rounded-md">
                  <div className="col-span-4">
                    <label className="text-xs text-gray-500 mb-1 block">Product (or type custom below)</label>
                    {item.product_id ? (
                      <div className="flex items-center justify-between px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md">
                        <span className="font-medium truncate">{item.product_name}</span>
                        <button type="button" onClick={() => updateItem(idx, 'product_id', '')} className="text-xs text-blue-600 hover:underline ml-2">Change</button>
                      </div>
                    ) : (
                      <ProductPicker
                        onSelect={(p) => {
                          const items = [...formData.items];
                          items[idx] = {
                            ...items[idx],
                            product_id: p.id,
                            product_name: p.name,
                            price: p.price || items[idx].price
                          };
                          setFormData({ ...formData, items });
                        }}
                        placeholder="Search inventory by name/brand/SKU/serial..."
                        testId={`qt-item-picker-${idx}`}
                      />
                    )}
                    <Input
                      value={item.product_name}
                      onChange={(e) => updateItem(idx, 'product_name', e.target.value)}
                      placeholder="…or enter a custom (non-inventory) item name"
                      className="mt-1"
                      data-testid={`qt-item-name-${idx}`}
                    />
                    {avail && (
                      <p className="text-[10px] mt-1 text-gray-500">
                        Available: <span className={avail.inStock > 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>{avail.inStock}</span> · Listed: {formatCurrency(avail.price, currency)}
                      </p>
                    )}
                  </div>
                  <div className="col-span-3">
                    <label className="text-xs text-gray-500 mb-1 block">Description (optional)</label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateItem(idx, 'description', e.target.value)}
                      data-testid={`qt-item-desc-${idx}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 mb-1 block">Qty</label>
                    <Input
                      type="number"
                      min="1"
                      value={item.qty}
                      onChange={(e) => updateItem(idx, 'qty', parseInt(e.target.value) || 1)}
                      data-testid={`qt-item-qty-${idx}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 mb-1 block">Price</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.price}
                      onChange={(e) => updateItem(idx, 'price', parseFloat(e.target.value) || 0)}
                      data-testid={`qt-item-price-${idx}`}
                    />
                  </div>
                  <div className="col-span-1 flex items-end justify-center">
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(idx)} disabled={formData.items.length === 1} data-testid={`qt-item-remove-${idx}`}>
                      <Trash size={14} className="text-red-600" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Totals & Meta */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-3">
            <Input label="Valid Until" type="date" value={formData.valid_until} onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })} data-testid="qt-valid-until" />
            <Select label="Status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} data-testid="qt-status">
              {QUOTATION_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Textarea label="Notes / Terms" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} data-testid="qt-notes" />
          </div>
          <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
              <span className="font-semibold" data-testid="qt-subtotal">{formatCurrency(subtotal, currency)}</span>
            </div>
            <div className="flex justify-between items-center">
              <label className="text-sm text-gray-600 dark:text-gray-400">Discount:</label>
              <Input type="number" step="0.01" min="0" value={formData.discount} onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })} className="w-28 text-right" data-testid="qt-discount" />
            </div>
            <div className="flex justify-between items-center">
              <label className="text-sm text-gray-600 dark:text-gray-400">Tax (%):</label>
              <Input type="number" step="0.01" min="0" value={formData.tax} onChange={(e) => setFormData({ ...formData, tax: parseFloat(e.target.value) || 0 })} className="w-28 text-right" data-testid="qt-tax" />
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Tax amount:</span>
              <span>{formatCurrency(taxAmt, currency)}</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-gray-300 dark:border-gray-700">
              <span className="text-lg font-bold">Grand Total:</span>
              <span className="text-lg font-bold text-blue-600 dark:text-blue-400" data-testid="qt-total">{formatCurrency(total, currency)}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={loading} data-testid="save-quotation">
            {loading ? 'Saving...' : (quotation ? 'Update Quotation' : 'Create Quotation')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
